import React, { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { DatePicker } from '@/components/ui/date-picker';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Filter,
  X,
  Check,
} from 'lucide-react';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { cn } from '@/lib/utils';

interface AdvancedFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
  availableEmployees: Array<{ id: number; name: string; department: string }>;
  availableDepartments: string[];
  availableProjects: Array<{ id: string; code: string; name: string }>;
  availableCostCentres: Array<{ id: string; code: string; name: string }>;
}

export const AdvancedFilterDrawer: React.FC<AdvancedFilterDrawerProps> = ({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  availableEmployees,
  availableDepartments,
  availableProjects,
  availableCostCentres,
}) => {
  // Local state for working with filters before applying
  const [workingFilters, setWorkingFilters] = useState<TimesheetReportFilters>(filters);
  


  // Sync working filters when dialog opens or filters prop changes
  useEffect(() => {
    if (open) {
      setWorkingFilters(filters);
    }
  }, [open, filters]);

  // Prepare options for MultiSelect components
  const employeeOptions = useMemo(
    () =>
      (availableEmployees || []).map((emp) => ({
        value: emp.id.toString(),
        label: `${emp.name} (${emp.department})`,
      })),
    [availableEmployees]
  );

  const departmentOptions = useMemo(
    () =>
      (availableDepartments || []).map((dept) => ({
        value: dept,
        label: dept,
      })),
    [availableDepartments]
  );

  const projectOptions = useMemo(
    () =>
      (availableProjects || []).map((proj) => ({
        value: proj.id.toString(),
        label: `${proj.code} - ${proj.name}`,
      })),
    [availableProjects]
  );

  const statusOptions = useMemo(
    () => [
      { value: 'draft', label: 'Draft' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'locked', label: 'Locked' },
    ],
    []
  );

  const groupingOptions = useMemo(
    () => [
      { value: 'weekEnding', label: 'Date' },
      { value: 'employeeName', label: 'Employee' },
      { value: 'status', label: 'Status' },
      { value: 'projectName', label: 'Project Name' },
      { value: 'projectCode', label: 'Project Code' },
      { value: 'costCentreCode', label: 'Cost Centre' },
      { value: 'billable', label: 'Billable' },
      { value: 'hasRejections', label: 'Rejections' },
    ],
    []
  );

  // Count total active filters
  const totalActiveFilters = useMemo(() => {
    let count = 0;
    if (workingFilters.dateFrom || workingFilters.dateTo) count++;
    if (workingFilters.employeeIds && workingFilters.employeeIds.length > 0) count++;
    if (workingFilters.departments && workingFilters.departments.length > 0) count++;
    if (workingFilters.projectIds && workingFilters.projectIds.length > 0) count++;
    if (workingFilters.statuses && workingFilters.statuses.length > 0) count++;
    if (workingFilters.rejectedOnly) count++;
    if (workingFilters.billableOnly) count++;
    if (workingFilters.groupBy && workingFilters.groupBy.length > 0) count++;
    return count;
  }, [workingFilters]);



  // Handler functions for MultiSelect
  const handleEmployeesChange = (values: string[]) => {
    const employeeIds = values.length > 0 ? values.map((v) => parseInt(v, 10)) : undefined;
    setWorkingFilters({ ...workingFilters, employeeIds });
  };

  const handleDepartmentsChange = (values: string[]) => {
    const departments = values.length > 0 ? values : undefined;
    setWorkingFilters({ ...workingFilters, departments });
  };

  const handleProjectsChange = (values: string[]) => {
    const projectIds = values.length > 0 ? values : undefined;
    setWorkingFilters({ ...workingFilters, projectIds });
  };

  const handleStatusesChange = (values: string[]) => {
    const statuses = values.length > 0 ? (values as Array<'draft' | 'submitted' | 'approved' | 'rejected' | 'locked'>) : undefined;
    setWorkingFilters({ ...workingFilters, statuses });
  };

  const handleGroupByChange = (values: string[]) => {
    const groupBy = values.length > 0 ? values : undefined;
    setWorkingFilters({ ...workingFilters, groupBy });
  };

  // Handle apply filters
  const handleApply = () => {
    onFiltersChange(workingFilters);
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setWorkingFilters(filters);
    onOpenChange(false);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    setWorkingFilters({});
  };

  // Detect mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "flex flex-col p-0 gap-0",
          isMobile ? "h-[90vh] rounded-t-2xl" : "w-[450px] sm:w-[450px] sm:max-w-none"
        )}
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-4 pb-3 flex-shrink-0 border-b">
          <SheetTitle className="text-base flex items-center gap-2">
            Filters
            <Badge variant="secondary" className="ml-2 flex items-center gap-1.5 px-2.5 py-0.5">
              <Filter className="h-3 w-3" />
              {totalActiveFilters} {totalActiveFilters === 1 ? 'filter' : 'filters'} active
            </Badge>
          </SheetTitle>
        </SheetHeader>

        {/* Filter Sections - Scrollable */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            <div className="px-6 py-4 space-y-3">

              {/* Grouping */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Group By</Label>
                <MultiSelect
                  options={groupingOptions}
                  value={workingFilters.groupBy || []}
                  onValueChange={handleGroupByChange}
                  placeholder="Select grouping..."
                  emptyMessage="No grouping options found."
                  maxDisplay={2}
                  showSearch={true}
                  badgeAccent="info"
                />
              </div>

              <Separator />

              {/* Date Range Filter */}
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">From Date</Label>
                    <DatePicker
                      date={workingFilters.dateFrom}
                      onDateChange={(date) =>
                        setWorkingFilters({
                          ...workingFilters,
                          dateFrom: date,
                        })
                      }
                      placeholder="Pick a date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">To Date</Label>
                    <DatePicker
                      date={workingFilters.dateTo}
                      onDateChange={(date) =>
                        setWorkingFilters({
                          ...workingFilters,
                          dateTo: date,
                        })
                      }
                      placeholder="Pick a date"
                      disabled={(date) =>
                        workingFilters.dateFrom ? date < workingFilters.dateFrom : false
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Employees Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Employees</Label>
                <MultiSelect
                  options={employeeOptions}
                  value={workingFilters.employeeIds?.map((id) => id.toString()) || []}
                  onValueChange={handleEmployeesChange}
                  placeholder="Select employees..."
                  emptyMessage="No employees found."
                  maxDisplay={2}
                />
              </div>

              <Separator />

              {/* Departments Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Departments</Label>
                <MultiSelect
                  options={departmentOptions}
                  value={workingFilters.departments || []}
                  onValueChange={handleDepartmentsChange}
                  placeholder="Select departments..."
                  emptyMessage="No departments found."
                  maxDisplay={2}
                />
              </div>

              <Separator />

              {/* Projects Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Projects</Label>
                <MultiSelect
                  options={projectOptions}
                  value={workingFilters.projectIds || []}
                  onValueChange={handleProjectsChange}
                  placeholder="Select projects..."
                  emptyMessage="No projects found."
                  maxDisplay={2}
                />
              </div>

              <Separator />

              {/* Status Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Timesheet Status</Label>
                <MultiSelect
                  options={statusOptions}
                  value={workingFilters.statuses || []}
                  onValueChange={handleStatusesChange}
                  placeholder="Select statuses..."
                  emptyMessage="No statuses found."
                  maxDisplay={2}
                  showSearch={false}
                />
              </div>

              <Separator />

              {/* Additional Filters */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Additional Filters</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="rejectedOnly"
                      checked={workingFilters.rejectedOnly || false}
                      onCheckedChange={(checked) => {
                        setWorkingFilters({ ...workingFilters, rejectedOnly: checked as boolean });
                      }}
                    />
                    <Label htmlFor="rejectedOnly" className="text-xs font-normal cursor-pointer">
                      With Rejections Only
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="billableOnly"
                      checked={workingFilters.billableOnly || false}
                      onCheckedChange={(checked) => {
                        setWorkingFilters({ ...workingFilters, billableOnly: checked as boolean });
                      }}
                    />
                    <Label htmlFor="billableOnly" className="text-xs font-normal cursor-pointer">
                      Billable Only
                    </Label>
                  </div>
                </div>
              </div>

            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Footer */}
        <Separator className="flex-shrink-0" />
        <SheetFooter className="px-6 py-4 flex-shrink-0 flex-row items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="gap-2"
            disabled={totalActiveFilters === 0}
          >
            <X className="h-4 w-4" />
            Clear All
          </Button>
          <Button onClick={handleApply} className="gap-2">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
